import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Modal } from '@/components/Modal';
import { useAuth } from '../contexts/AuthContext';
import { knowledgeBaseService, KnowledgeArticleSummary, KnowledgeCategory, CreateKnowledgeArticleRequest, CreateKnowledgeCategoryRequest, KnowledgeArticleStatus, KnowledgeArticleType, KnowledgeAnalytics } from '../services/knowledgeBaseService';
import { exportService } from '../services/exportService';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { Loading } from '../components/Loading';
import { Badge } from '../components/Badge';
import RichTextEditor from '../components/RichTextEditor';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

interface KnowledgeBaseAdminProps {}

export function hasArticleContent(content: string): boolean {
  try {
    const nodes = JSON.parse(content);
    if (Array.isArray(nodes)) {
      const text = (node: any): string => typeof node?.text === 'string'
        ? node.text : Array.isArray(node?.children) ? node.children.map(text).join('') : '';
      return nodes.map(text).join('').trim().length > 0;
    }
  } catch { /* Existing articles may contain plain text or HTML. */ }
  const document = new DOMParser().parseFromString(content, 'text/html');
  return !!document.body.textContent?.trim();
}

export const KnowledgeBaseAdmin: React.FC<KnowledgeBaseAdminProps> = () => {
  useDocumentTitle('Knowledge Base Admin | SupportDesk');
  const { user, isAdmin, isAgent } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedArticleId = searchParams.get('edit');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [editorError, setEditorError] = useState<string | null>(null);
  
  // Data state
  const [articlePage, setArticlePage] = useState(1);
  const [articles, setArticles] = useState<KnowledgeArticleSummary[]>([]);
  const [categories, setCategories] = useState<KnowledgeCategory[]>([]);
  const [analytics, setAnalytics] = useState<KnowledgeAnalytics | null>(null);
  
  // UI state
  const [currentView, setCurrentView] = useState<'overview' | 'articles' | 'categories' | 'analytics'>('overview');
  const [showArticleModal, setShowArticleModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingArticle, setEditingArticle] = useState<Pick<KnowledgeArticleSummary, 'id'> | null>(null);
  const [editingCategory, setEditingCategory] = useState<KnowledgeCategory | null>(null);
  
  // Form state
  const [articleForm, setArticleForm] = useState<CreateKnowledgeArticleRequest>({
    title: '',
    summary: '',
    content: '',
    htmlContent: '',
    type: KnowledgeArticleType.General,
    status: KnowledgeArticleStatus.Draft,
    categoryId: '',
    tags: '',
    keywords: '',
    isPublic: false,
    isFeatured: false,
    collaboratorIds: []
  });

  const [categoryForm, setCategoryForm] = useState<CreateKnowledgeCategoryRequest>({
    name: '',
    description: '',
    parentCategoryId: '',
    displayOrder: 0,
    isActive: true,
    isPublic: true,
    icon: '',
    color: '#3B82F6'
  });

  useEffect(() => {
    loadData();
  }, [currentView, articlePage]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (currentView === 'overview') {
        const [articlesResponse, categoriesResponse, analyticsResponse] = await Promise.all([
          knowledgeBaseService.getArticles({ page: 1, pageSize: 10 }),
          knowledgeBaseService.getCategories(false),
          knowledgeBaseService.getAnalytics()
        ]);
        setArticles(articlesResponse);
        setCategories(categoriesResponse);
        setAnalytics(analyticsResponse);
      } else if (currentView === 'articles') {
        const articlesResponse = await knowledgeBaseService.getArticles({ page: articlePage, pageSize: 50 });
        setArticles(articlesResponse);
      } else if (currentView === 'categories') {
        const categoriesResponse = await knowledgeBaseService.getCategories(false);
        setCategories(categoriesResponse);
      } else if (currentView === 'analytics') {
        const analyticsResponse = await knowledgeBaseService.getAnalytics();
        setAnalytics(analyticsResponse);
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateArticle = async () => {
    if (savingRef.current) return;
    if (!articleForm.title.trim() || !hasArticleContent(articleForm.content)) {
      setEditorError('Enter a title and article content before saving.');
      return;
    }
    savingRef.current = true;
    setSaving(true);
    setEditorError(null);
    try {
      if (editingArticle) {
        const { collaboratorIds, ...update } = articleForm;
        await knowledgeBaseService.updateArticle(editingArticle.id, update);
      } else {
        await knowledgeBaseService.createArticle(articleForm);
      }
      setShowArticleModal(false);
      resetArticleForm();
      await loadData();
    } catch (err) {
      console.error('Error creating article:', err);
      setEditorError('Unable to save the article. Your changes are still here; please try again.');
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const handleCreateCategory = async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setEditorError(null);
    try {
      if (editingCategory) {
        await knowledgeBaseService.updateCategory(editingCategory.id, categoryForm);
      } else {
        await knowledgeBaseService.createCategory(categoryForm);
      }
      setShowCategoryModal(false);
      resetCategoryForm();
      await loadData();
    } catch (err) {
      console.error('Error creating category:', err);
      setEditorError('Unable to save the category. Your changes are still here; please try again.');
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const handleEditArticle = async (summary: Pick<KnowledgeArticleSummary, 'id'>) => {
    setError(null);
    try {
      const article = await knowledgeBaseService.getArticle(summary.id, false);
      setArticleForm({
        title: article.title, summary: article.summary ?? '',
        content: article.content, htmlContent: article.htmlContent ?? '',
        type: article.type, status: article.status, categoryId: article.categoryId ?? '',
        tags: article.tags ?? '', keywords: article.keywords ?? '',
        isPublic: article.isPublic, isFeatured: article.isFeatured,
      });
      setEditingArticle(summary);
      setEditorError(null);
      setShowArticleModal(true);
    } catch {
      setError('Unable to load this article for editing. Please try again.');
    }
  };

  useEffect(() => {
    if (!requestedArticleId || (!isAdmin() && !isAgent())) return;
    void handleEditArticle({ id: requestedArticleId });
    const remainingParams = new URLSearchParams(searchParams);
    remainingParams.delete('edit');
    setSearchParams(remainingParams, { replace: true });
  }, [requestedArticleId]);

  const handleEditCategory = (category: KnowledgeCategory) => {
    setCategoryForm({
      name: category.name, description: category.description ?? '',
      parentCategoryId: category.parentCategoryId ?? '', displayOrder: category.displayOrder,
      isActive: category.isActive, isPublic: category.isPublic,
      icon: category.icon ?? '', color: category.color ?? '#3B82F6',
    });
    setEditingCategory(category);
    setEditorError(null);
    setShowCategoryModal(true);
  };

  const handlePublishArticle = async (articleId: string) => {
    try {
      await knowledgeBaseService.publishArticle(articleId);
      await loadData();
    } catch (err) {
      console.error('Error publishing article:', err);
      setError('Failed to publish article. Please try again.');
    }
  };

  const handleArchiveArticle = async (articleId: string) => {
    try {
      await knowledgeBaseService.archiveArticle(articleId);
      await loadData();
    } catch (err) {
      console.error('Error archiving article:', err);
      setError('Failed to archive article. Please try again.');
    }
  };

  const handleExportArticles = async () => {
    try {
      setError(null);
      await exportService.exportKnowledgeBase({
        publicOnly: false, // As admin, export all articles
      });
    } catch (err) {
      console.error('Error exporting articles:', err);
      setError('Failed to export articles');
    }
  };

  const resetArticleForm = () => {
    setArticleForm({
      title: '',
      summary: '',
      content: '',
      htmlContent: '',
      type: KnowledgeArticleType.General,
      status: KnowledgeArticleStatus.Draft,
      categoryId: '',
      tags: '',
      keywords: '',
      isPublic: false,
      isFeatured: false,
      collaboratorIds: []
    });
    setEditingArticle(null);
  };

  const resetCategoryForm = () => {
    setCategoryForm({
      name: '',
      description: '',
      parentCategoryId: '',
      displayOrder: 0,
      isActive: true,
      isPublic: true,
      icon: '',
      color: '#3B82F6'
    });
    setEditingCategory(null);
  };

  const getStatusColor = (status: KnowledgeArticleStatus): string => {
    const colors = {
      [KnowledgeArticleStatus.Draft]: 'bg-gray-100 text-gray-800',
      [KnowledgeArticleStatus.Review]: 'bg-yellow-100 text-yellow-800',
      [KnowledgeArticleStatus.Published]: 'bg-green-100 text-green-800',
      [KnowledgeArticleStatus.Archived]: 'bg-red-100 text-red-800',
      [KnowledgeArticleStatus.Deprecated]: 'bg-purple-100 text-purple-800'
    };
    return colors[status] || colors[KnowledgeArticleStatus.Draft];
  };

  const getStatusLabel = (status: KnowledgeArticleStatus): string => {
    const labels = {
      [KnowledgeArticleStatus.Draft]: 'Draft',
      [KnowledgeArticleStatus.Review]: 'Review',
      [KnowledgeArticleStatus.Published]: 'Published',
      [KnowledgeArticleStatus.Archived]: 'Archived',
      [KnowledgeArticleStatus.Deprecated]: 'Deprecated'
    };
    return labels[status] || 'Draft';
  };

  const getTypeLabel = (type: KnowledgeArticleType): string => {
    const labels = {
      [KnowledgeArticleType.General]: 'General',
      [KnowledgeArticleType.HowTo]: 'How To',
      [KnowledgeArticleType.Troubleshooting]: 'Troubleshooting',
      [KnowledgeArticleType.FAQ]: 'FAQ',
      [KnowledgeArticleType.Policy]: 'Policy',
      [KnowledgeArticleType.Training]: 'Training',
      [KnowledgeArticleType.BestPractice]: 'Best Practice'
    };
    return labels[type] || 'General';
  };

  // Check if user has permission to manage knowledge base
  if (!user || (!isAdmin() && !isAgent())) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card>
          <div className="p-8 text-center">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Access Denied</h3>
            <p className="text-gray-500">
              You don't have permission to access the Knowledge Base admin panel.
            </p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Knowledge Base Admin</h1>
        <p className="text-gray-600">
          Manage articles, categories, and knowledge base settings
        </p>
      </div>

      {/* Navigation Tabs */}
      <div className="border-b border-gray-200 mb-8">
        <nav className="-mb-px flex flex-wrap gap-x-6 gap-y-2">
          {[
            { key: 'overview', label: 'Overview' },
            { key: 'articles', label: 'Articles' },
            { key: 'categories', label: 'Categories' },
            { key: 'analytics', label: 'Analytics' }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setCurrentView(tab.key as any)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                currentView === tab.key
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-800">{error}</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setError(null);
              loadData();
            }}
            className="mt-2"
          >
            Retry
          </Button>
        </div>
      )}

      {loading && currentView !== 'overview' ? (
        <div className="flex items-center justify-center h-64">
          <Loading size="lg" />
        </div>
      ) : (
        <>
          {/* Overview */}
          {currentView === 'overview' && analytics && (
            <div className="space-y-8">
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <Card>
                  <div className="p-6">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                          <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-500">Total Articles</p>
                        <p className="text-lg font-semibold text-gray-900">{analytics.totalArticles}</p>
                      </div>
                    </div>
                  </div>
                </Card>

                <Card>
                  <div className="p-6">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                          <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        </div>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-500">Published</p>
                        <p className="text-lg font-semibold text-gray-900">{analytics.publishedArticles}</p>
                      </div>
                    </div>
                  </div>
                </Card>

                <Card>
                  <div className="p-6">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center">
                          <svg className="w-4 h-4 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                            <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-500">Total Views</p>
                        <p className="text-lg font-semibold text-gray-900">{analytics.totalViews.toLocaleString()}</p>
                      </div>
                    </div>
                  </div>
                </Card>

                <Card>
                  <div className="p-6">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                          <svg className="w-4 h-4 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                        </div>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-500">Avg Rating</p>
                        <p className="text-lg font-semibold text-gray-900">{analytics.averageRating.toFixed(1)}</p>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>

              {/* Recent Articles */}
              <Card>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-900">Recent Articles</h3>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentView('articles')}
                    >
                      View All
                    </Button>
                  </div>
                  <div className="space-y-3">
                    {articles.slice(0, 5).map(article => (
                      <div key={article.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900">{article.title}</h4>
                          <div className="flex items-center space-x-2 mt-1">
                            <Badge className={getStatusColor(article.status)}>
                              {getStatusLabel(article.status)}
                            </Badge>
                            <span className="text-sm text-gray-500">
                              {article.viewCount} views
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          {article.status !== KnowledgeArticleStatus.Published && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handlePublishArticle(article.id)}
                            >
                              Publish
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* Articles View */}
          {currentView === 'articles' && (
            <div className="space-y-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-xl font-semibold text-gray-900">Articles</h2>
                <div className="flex space-x-3">
                  <Button
                    variant="outline"
                    onClick={handleExportArticles}
                    disabled={loading}
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                    </svg>
                    Export CSV
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => { resetArticleForm(); setEditorError(null); setShowArticleModal(true); }}
                    data-testid="create-article-button"
                  >
                    Create Article
                  </Button>
                </div>
              </div>

              <div className="space-y-4">
                {articles.map(article => (
                  <Card key={article.id} padding="none">
                    <div className="p-6">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <Badge className={getStatusColor(article.status)}>
                              {getStatusLabel(article.status)}
                            </Badge>
                            <Badge className="bg-blue-100 text-blue-800">
                              {getTypeLabel(article.type)}
                            </Badge>
                            {article.isFeatured && (
                              <Badge className="bg-yellow-100 text-yellow-800">Featured</Badge>
                            )}
                          </div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-2">
                            {article.title}
                          </h3>
                          {article.summary && (
                            <p className="text-gray-600 mb-3">{article.summary}</p>
                          )}
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500">
                            <span>By {article.authorName}</span>
                            <span>{article.viewCount} views</span>
                            <span>{article.ratingCount} ratings</span>
                            <span>{new Date(article.createdAt).toLocaleDateString()}</span>
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 mt-3 sm:mt-0 sm:ml-4">
                          <Button variant="outline" size="sm" onClick={() => handleEditArticle(article)} aria-label={`Edit ${article.title}`}>Edit</Button>
                          <Link to={`/knowledge-base/articles/${article.id}`} className="text-sm text-primary-600 underline" aria-label={`View ${article.title}`}>View</Link>
                          {article.status !== KnowledgeArticleStatus.Published && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handlePublishArticle(article.id)}
                            >
                              Publish
                            </Button>
                          )}
                          {article.status === KnowledgeArticleStatus.Published && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleArchiveArticle(article.id)}
                            >
                              Archive
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
              {articles.length === 0 && <p className="text-gray-600">No articles on this page.</p>}
              <nav aria-label="Article pages" className="flex items-center justify-between gap-3">
                <Button variant="outline" disabled={articlePage === 1 || loading} onClick={() => setArticlePage(page => page - 1)}>Previous page</Button>
                <span>Page {articlePage}</span>
                <Button variant="outline" disabled={articles.length < 50 || loading} onClick={() => setArticlePage(page => page + 1)}>Next page</Button>
              </nav>
            </div>
          )}

          {/* Categories View */}
          {currentView === 'categories' && (
            <div className="space-y-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-xl font-semibold text-gray-900">Categories</h2>
                <Button
                  variant="primary"
                  onClick={() => { resetCategoryForm(); setEditorError(null); setShowCategoryModal(true); }}
                  data-testid="create-category-button"
                >
                  Create Category
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {categories.map(category => (
                  <Card key={category.id}>
                    <div className="p-6">
                      <div className="flex items-center space-x-3 mb-3">
                        {category.icon && (
                          <span className="text-2xl">{category.icon}</span>
                        )}
                        <h3 className="text-lg font-semibold text-gray-900">
                          {category.name}
                        </h3>
                      </div>
                      <Button variant="outline" size="sm" className="mb-3" onClick={() => handleEditCategory(category)} aria-label={`Edit ${category.name}`}>Edit</Button>
                      {category.description && (
                        <p className="text-gray-600 mb-3">{category.description}</p>
                      )}
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <span className="text-sm text-gray-500">
                          {category.articleCount} articles
                        </span>
                        <div className="flex items-center space-x-2">
                          {category.isPublic && (
                            <Badge className="bg-green-100 text-green-800">Public</Badge>
                          )}
                          {category.isActive && (
                            <Badge className="bg-blue-100 text-blue-800">Active</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Analytics View */}
          {currentView === 'analytics' && analytics && (
            <div className="space-y-8">
              <h2 className="text-xl font-semibold text-gray-900">Analytics</h2>

              {/* Top Articles */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <Card>
                  <div className="p-6">
                    <h3 className="font-medium text-gray-900 mb-4">Top Viewed Articles</h3>
                    <div className="space-y-3">
                      {analytics.topViewedArticles.map((article, index) => (
                        <div key={article.id} className="flex flex-wrap items-center justify-between gap-3">
                          <div className="flex items-center space-x-3">
                            <span className="text-sm font-semibold text-gray-400 w-4">
                              #{index + 1}
                            </span>
                            <span className="font-medium text-gray-900 truncate">
                              {article.title}
                            </span>
                          </div>
                          <span className="text-sm text-gray-500">
                            {article.viewCount} views
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </Card>

                <Card>
                  <div className="p-6">
                    <h3 className="font-medium text-gray-900 mb-4">Top Rated Articles</h3>
                    <div className="space-y-3">
                      {analytics.topRatedArticles.map((article, index) => (
                        <div key={article.id} className="flex flex-wrap items-center justify-between gap-3">
                          <div className="flex items-center space-x-3">
                            <span className="text-sm font-semibold text-gray-400 w-4">
                              #{index + 1}
                            </span>
                            <span className="font-medium text-gray-900 truncate">
                              {article.title}
                            </span>
                          </div>
                          <span className="text-sm text-gray-500">
                            {article.rating.toFixed(1)} ★
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          )}
        </>
      )}

      {/* Create Article Modal */}
      <Modal
        isOpen={showArticleModal}
        title={editingArticle ? 'Edit Article' : 'Create New Article'}
        size="xl"
        className="max-h-[90dvh] overflow-y-auto"
        closable={!saving}
        onClose={() => {
          if (savingRef.current) return;
          setShowArticleModal(false);
          resetArticleForm();
        }}
      >
        {editorError && <div role="alert" className="mb-4 rounded bg-red-50 p-3 text-red-700">{editorError}</div>}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title *
                  </label>
                  <Input
                    type="text"
                    aria-label="Article title"
                    value={articleForm.title}
                    onChange={(e) => setArticleForm({ ...articleForm, title: e.target.value })}
                    placeholder="Enter article title"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Summary
                  </label>
                  <textarea
                    aria-label="Summary"
                    value={articleForm.summary}
                    onChange={(e) => setArticleForm({ ...articleForm, summary: e.target.value })}
                    placeholder="Brief summary of the article"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Type
                    </label>
                    <select
                      aria-label="Article type"
                    value={articleForm.type}
                      onChange={(e) => setArticleForm({ ...articleForm, type: Number(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value={KnowledgeArticleType.General}>General</option>
                      <option value={KnowledgeArticleType.HowTo}>How To</option>
                      <option value={KnowledgeArticleType.Troubleshooting}>Troubleshooting</option>
                      <option value={KnowledgeArticleType.FAQ}>FAQ</option>
                      <option value={KnowledgeArticleType.Policy}>Policy</option>
                      <option value={KnowledgeArticleType.Training}>Training</option>
                      <option value={KnowledgeArticleType.BestPractice}>Best Practice</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      aria-label="Article status"
                    value={articleForm.status}
                      onChange={(e) => setArticleForm({ ...articleForm, status: Number(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value={KnowledgeArticleStatus.Draft}>Draft</option>
                      <option value={KnowledgeArticleStatus.Review}>Review</option>
                      <option value={KnowledgeArticleStatus.Published}>Published</option>
                  <option value={KnowledgeArticleStatus.Archived}>Archived</option>
                  <option value={KnowledgeArticleStatus.Deprecated}>Deprecated</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Category
                    </label>
                    <select
                      aria-label="Category"
                    value={articleForm.categoryId}
                      onChange={(e) => setArticleForm({ ...articleForm, categoryId: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select category</option>
                      {categories.map(category => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Content *
                  </label>
                  <RichTextEditor
                    key={editingArticle?.id ?? 'new'}
                    value={articleForm.content}
                    onChange={(content) =>
                      setArticleForm({ ...articleForm, content, htmlContent: '' })
                    }
                    placeholder="Write your article content here..."
                    disabled={saving}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tags (comma-separated)
                    </label>
                    <Input
                      type="text"
                      aria-label="Tags"
                    value={articleForm.tags}
                      onChange={(e) => setArticleForm({ ...articleForm, tags: e.target.value })}
                      placeholder="support, troubleshooting, faq"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Keywords (comma-separated)
                    </label>
                    <Input
                      type="text"
                      aria-label="Keywords"
                    value={articleForm.keywords}
                      onChange={(e) => setArticleForm({ ...articleForm, keywords: e.target.value })}
                      placeholder="search keywords for this article"
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={articleForm.isPublic}
                      onChange={(e) => setArticleForm({ ...articleForm, isPublic: e.target.checked })}
                      className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200"
                    />
                    <span className="ml-2 text-sm text-gray-700">Public (visible to all users)</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={articleForm.isFeatured}
                      onChange={(e) => setArticleForm({ ...articleForm, isFeatured: e.target.checked })}
                      className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200"
                    />
                    <span className="ml-2 text-sm text-gray-700">Featured</span>
                  </label>
                </div>
              </div>

              <div className="flex items-center justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
                <Button
                  variant="outline"
                  onClick={() => {
                    if (savingRef.current) return;
                    setShowArticleModal(false);
                    resetArticleForm();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleCreateArticle}
                  disabled={!articleForm.title.trim() || !hasArticleContent(articleForm.content) || saving}
                >
                  {saving ? 'Saving...' : (editingArticle ? 'Update' : 'Create')} Article
                </Button>
              </div>
      </Modal>

      {/* Create Category Modal */}
      <Modal
        isOpen={showCategoryModal}
        title={editingCategory ? 'Edit Category' : 'Create New Category'}
        size="xl"
        className="max-h-[90dvh] overflow-y-auto"
        closable={!saving}
        onClose={() => {
          if (savingRef.current) return;
          setShowCategoryModal(false);
          resetCategoryForm();
        }}
      >
        {editorError && <div role="alert" className="mb-4 rounded bg-red-50 p-3 text-red-700">{editorError}</div>}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name *
                  </label>
                  <Input
                    type="text"
                    aria-label="Category name"
                    value={categoryForm.name}
                    onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                    placeholder="Category name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    aria-label="Description"
                    value={categoryForm.description}
                    onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                    placeholder="Category description"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Parent Category
                  </label>
                  <select
                    aria-label="Parent category"
                    value={categoryForm.parentCategoryId}
                    onChange={(e) => setCategoryForm({ ...categoryForm, parentCategoryId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">None (Root Category)</option>
                    {categories.filter(category => category.id !== editingCategory?.id).map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Icon (Emoji)
                    </label>
                    <Input
                      type="text"
                      aria-label="Icon"
                    value={categoryForm.icon}
                      onChange={(e) => setCategoryForm({ ...categoryForm, icon: e.target.value })}
                      placeholder="📚"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Color
                    </label>
                    <input
                      type="color"
                      value={categoryForm.color || '#3B82F6'}
                      onChange={(e) => setCategoryForm({ ...categoryForm, color: e.target.value })}
                      className="w-full h-10 rounded border border-gray-300"
                      data-testid="category-color-input"
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={categoryForm.isActive}
                      onChange={(e) => setCategoryForm({ ...categoryForm, isActive: e.target.checked })}
                      className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200"
                    />
                    <span className="ml-2 text-sm text-gray-700">Active</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={categoryForm.isPublic}
                      onChange={(e) => setCategoryForm({ ...categoryForm, isPublic: e.target.checked })}
                      className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200"
                    />
                    <span className="ml-2 text-sm text-gray-700">Public</span>
                  </label>
                </div>
              </div>

              <div className="flex items-center justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
                <Button
                  variant="outline"
                  onClick={() => {
                    if (savingRef.current) return;
                    setShowCategoryModal(false);
                    resetCategoryForm();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleCreateCategory}
                  disabled={!categoryForm.name.trim() || saving}
                >
                  {saving ? 'Saving...' : (editingCategory ? 'Update' : 'Create')} Category
                </Button>
              </div>
      </Modal>
    </div>
  );
};

export default KnowledgeBaseAdmin;
